import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { 
  Loader2,
  Package,
  Search,
  Star,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Filter,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const stockStatusColors = {
  available: 'bg-success/10 text-success border-success/20',
  low: 'bg-warning/10 text-warning border-warning/20',
  out_of_stock: 'bg-destructive/10 text-destructive border-destructive/20',
};

const stockStatusLabels = {
  available: 'Available',
  low: 'Low Stock',
  out_of_stock: 'Out of Stock',
};

const categoryLabels = {
  acne: 'Acne',
  pigmentation: 'Pigmentation',
  eczema: 'Eczema',
  psoriasis: 'Psoriasis',
  fungal: 'Fungal',
  bacterial: 'Bacterial',
  viral: 'Viral',
  antihistamine: 'Antihistamine',
  moisturizer: 'Moisturizer',
  sunscreen: 'Sunscreen',
  'anti-aging': 'Anti-Aging',
  hair_loss: 'Hair Loss',
  other: 'Other',
};

const formLabels = {
  tablet: 'Tablet',
  capsule: 'Capsule',
  cream: 'Cream',
  ointment: 'Ointment',
  gel: 'Gel',
  lotion: 'Lotion',
  solution: 'Solution',
  injection: 'Injection',
  powder: 'Powder',
  shampoo: 'Shampoo',
  serum: 'Serum',
  drops: 'Drops',
  spray: 'Spray',
  other: 'Other',
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function PharmacyInventoryPage() {
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [updatingId, setUpdatingId] = useState(null);

  const fetchMedicines = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (stockFilter !== 'all') params.append('stock_status', stockFilter);
      params.append('is_active', 'true');
      
      const { data } = await axios.get(`${API_URL}/api/medicines?${params}`, { withCredentials: true });
      setMedicines(data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to fetch medicines');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, categoryFilter, stockFilter]);

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/medicines/categories`, { withCredentials: true });
      setCategories(data.categories);
    } catch (error) {
      console.error('Failed to fetch categories', error);
    }
  }, []);

  useEffect(() => {
    fetchMedicines();
    fetchCategories();
  }, [fetchMedicines, fetchCategories]);

  const updateStock = async (medicineId, newStatus) => {
    setUpdatingId(medicineId);
    try {
      await axios.patch(
        `${API_URL}/api/medicines/${medicineId}/stock`,
        { stock_status: newStatus },
        { withCredentials: true }
      );
      toast.success('Stock status updated');
      fetchMedicines();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to update stock');
    } finally {
      setUpdatingId(null);
    }
  };

  const stats = {
    total: medicines.length,
    available: medicines.filter(m => m.stock_status === 'available').length,
    low: medicines.filter(m => m.stock_status === 'low').length,
    outOfStock: medicines.filter(m => m.stock_status === 'out_of_stock').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="pharmacy-inventory-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="pharmacy-inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-medium tracking-tight text-foreground">
            Pharmacy Inventory
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage stock levels and track medicine availability
          </p>
        </div>
        <Button 
          onClick={() => fetchMedicines()}
          variant="outline"
          className="rounded-full"
          data-testid="refresh-inventory-button"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Package className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Total Medicines</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-success/10">
              <CheckCircle className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold">{stats.available}</p>
              <p className="text-xs text-muted-foreground">In Stock</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStockFilter('low')}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-warning/10">
              <AlertTriangle className="w-5 h-5 text-warning" />
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold">{stats.low}</p>
              <p className="text-xs text-muted-foreground">Low Stock</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStockFilter('out_of_stock')}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-destructive/10">
              <XCircle className="w-5 h-5 text-destructive" />
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold">{stats.outOfStock}</p>
              <p className="text-xs text-muted-foreground">Out of Stock</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Table */}
      <Card className="border-border shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <CardTitle className="font-heading text-lg">Stock Management</CardTitle>
              <CardDescription>Update stock status for medicines</CardDescription>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search medicines..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 rounded-xl"
                  data-testid="search-pharmacy-input"
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-40 rounded-xl" data-testid="pharmacy-category-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{categoryLabels[cat] || cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={stockFilter} onValueChange={setStockFilter}>
                <SelectTrigger className="w-full sm:w-40 rounded-xl" data-testid="pharmacy-stock-filter">
                  <SelectValue placeholder="Stock" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stock</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="low">Low Stock</SelectItem>
                  <SelectItem value="out_of_stock">Out of Stock</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Medicine</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Form</TableHead>
                  <TableHead>Current Stock</TableHead>
                  <TableHead>Update Stock</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {medicines.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No medicines found
                    </TableCell>
                  </TableRow>
                ) : (
                  medicines.map((medicine) => (
                    <TableRow key={medicine.id} className="table-row-hover" data-testid={`pharmacy-medicine-row-${medicine.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {medicine.is_preferred && <Star className="w-4 h-4 text-warning fill-warning" />}
                          <div>
                            <p className="font-medium">{medicine.name}</p>
                            <p className="text-xs text-muted-foreground">{medicine.generic_name} • {medicine.strength}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{medicine.brand}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {categoryLabels[medicine.category] || medicine.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="capitalize">{formLabels[medicine.form] || medicine.form}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={stockStatusColors[medicine.stock_status]}>
                          {stockStatusLabels[medicine.stock_status]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={medicine.stock_status} 
                          onValueChange={(value) => updateStock(medicine.id, value)}
                          disabled={updatingId === medicine.id}
                        >
                          <SelectTrigger className="w-36 rounded-xl" data-testid={`update-stock-${medicine.id}`}>
                            {updatingId === medicine.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <SelectValue />
                            )}
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="available">
                              <span className="flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-success" /> Available
                              </span>
                            </SelectItem>
                            <SelectItem value="low">
                              <span className="flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-warning" /> Low Stock
                              </span>
                            </SelectItem>
                            <SelectItem value="out_of_stock">
                              <span className="flex items-center gap-2">
                                <XCircle className="w-4 h-4 text-destructive" /> Out of Stock
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
